import Clipboard from "@react-native-clipboard/clipboard";
import {
    Button,
    CheckBox,
    ConfirmModal,
    DataTable,
    IconButton,
    Loading,
    PageTitle,
    Spacer,
    StyleFunction,
    Surface,
    Text,
    useModal,
    usePagination,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    GQSaftJob,
    GQSaftJobsQuery,
    useRebuildSaftJobsMutation,
    useSaftJobsQuery,
} from "graphql-sdk";
import { formatDateTime } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useMemo } from "react";
import { View } from "react-native";
import { useHandleMutationError, useMultiSelect } from "../../hooks";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";

export function SaftJobsListScreen() {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const toast = useToast();
    const { render } = useModal();
    const [{ locale }] = useAdminSession(["locale"]);

    const [rebuildSaftJobs] = useRebuildSaftJobsMutation();
    const { handleMutationError } = useHandleMutationError();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "createdAt",
        initialSortDirection: "DESC",
    });

    const { data, loading, refetch } = useSaftJobsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const copyOnClick = useCallback(
        (text: string) => {
            Clipboard.setString(text);
            toast.info(t("backoffice.info.text_copied", "Text copied"));
        },
        [t, toast]
    );

    const {
        selectedItems,
        setSelectedItems,
        handleMultiSelect,
        allItemsSelected: allRowsSelected,
        setAllItemsSelected: setAllRowsSelected,
    } = useMultiSelect<GQSaftJob["id"]>();

    const selectedJobsSet = useMemo(
        () => new Set(selectedItems),
        [selectedItems]
    );

    const selectedCountText = useMemo(() => {
        if (selectedItems.length === 0) {
            return "";
        }

        return ` (${selectedItems.length})`;
    }, [selectedItems]);

    const startRebuild = useCallback(async () => {
        const deleteResponse = await render(onClose => (
            <ConfirmModal
                headerText={t("backoffice.saft.rebuild", "Rebuild")}
                bodyText={t(
                    "backoffice.saft.rebuild_shifts",
                    "Rebuild shifts?"
                )}
                onClose={onClose}
            />
        ));

        if (!deleteResponse) {
            return;
        }

        await handleMutationError(
            async () => {
                if (!data) {
                    return;
                }
                // Find jobs from the ids and return array of shift ids and only jobs with a shift id
                const shiftIds = data.saftJobs.data
                    .filter(
                        itr => itr.shiftId && selectedItems.includes(itr.id)
                    )
                    .map(itm => itm.shiftId);
                // Make the shift id list unique as only one job will be started per shift id
                const uniqShiftIds = [...new Set(shiftIds)];
                return await rebuildSaftJobs({
                    variables: { shiftIds: uniqShiftIds },
                });
            },
            t("backoffice.saft.rebuild_started", "Rebuilding started"),
            async () => {
                await refetch();
            }
        );

        setSelectedItems([]);
        setAllRowsSelected(false);
    }, [
        data,
        handleMutationError,
        rebuildSaftJobs,
        refetch,
        render,
        selectedItems,
        setAllRowsSelected,
        setSelectedItems,
        t,
    ]);

    const renderJobRow = useCallback(
        (item: GQSaftJobsQuery["saftJobs"]["data"][0], rowIndex) => {
            const selectedJob = selectedJobsSet.has(item.id);

            return (
                <DataTable.Row
                    style={rowIndex % 2 === 1 ? styles.oddRow : undefined}
                    key={`${item.id}-${item.runAt}`}
                >
                    <DataTable.Cell style={styles.iconColumn}>
                        {item.shiftId && (
                            <CheckBox
                                value={selectedJob}
                                onValueChange={value =>
                                    handleMultiSelect(value, [item.id])
                                }
                                style={styles.checkbox}
                                testID={"select:" + item.id}
                            />
                        )}
                    </DataTable.Cell>
                    <DataTable.Cell numeric style={styles.mediumColumn}>
                        <Text numberOfLines={1}>{item.id}</Text>
                        <IconButton
                            size="small"
                            onPress={() => copyOnClick(item.id.toString())}
                            name="copy"
                            color="black"
                        />
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.veryLargeColumn}>
                        <Text numberOfLines={1}>{item.shiftId}</Text>
                        {/* If the item does not have a shift id we cant start rebuild job -> no checkbox */}
                        {item.shiftId && (
                            <IconButton
                                size="small"
                                onPress={() => copyOnClick(item.shiftId)}
                                name="copy"
                                color="black"
                            />
                        )}
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.largeColumn}>
                        {item?.shift?.merchant.name}
                    </DataTable.Cell>
                    <DataTable.Cell numeric style={styles.smallColumn}>
                        {item.attempts}
                    </DataTable.Cell>
                    <DataTable.Cell>
                        <Text numberOfLines={1}>{item.lastError}</Text>
                        {item.lastError && (
                            <IconButton
                                size="small"
                                onPress={() => copyOnClick(item.lastError)}
                                name="copy"
                                color="black"
                            />
                        )}
                    </DataTable.Cell>
                    <DataTable.Cell numeric style={styles.largeColumn}>
                        {item.runAt &&
                            formatDateTime(
                                item.runAt,
                                locale as AvailableLocale
                            )}
                    </DataTable.Cell>
                    <DataTable.Cell numeric style={styles.largeColumn}>
                        {item.createdAt &&
                            formatDateTime(
                                item.createdAt,
                                locale as AvailableLocale
                            )}
                    </DataTable.Cell>
                </DataTable.Row>
            );
        },
        [
            selectedJobsSet,
            styles.oddRow,
            styles.iconColumn,
            styles.checkbox,
            styles.mediumColumn,
            styles.veryLargeColumn,
            styles.smallColumn,
            styles.largeColumn,
            locale,
            handleMultiSelect,
            copyOnClick,
        ]
    );

    return (
        <AdminContainer>
            <PageTitle>
                {t("backoffice.saft.saft_jobs_list", "SAF-T jobs")}
            </PageTitle>

            <View style={styles.container}>
                <View style={styles.actionButtonContainer}>
                    <Button
                        disabled={!selectedItems.length}
                        onPress={startRebuild}
                    >
                        {`${t(
                            "backoffice.saft.rebuild",
                            "Rebuild"
                        )}${selectedCountText}`}
                    </Button>
                </View>
            </View>
            <Spacer space={2} />
            <Surface>
                {data && data.saftJobs.data.length === 0 ? (
                    <Text>{t("common.no_data", "No data")}</Text>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title style={styles.iconColumn}>
                                <CheckBox
                                    value={allRowsSelected}
                                    onValueChange={value => {
                                        const allItemIds = data.saftJobs.data
                                            .filter(itr => itr.shiftId)
                                            .map(p => p.id);
                                        handleMultiSelect(value, allItemIds);
                                        setAllRowsSelected(value);
                                    }}
                                />
                            </DataTable.Title>
                            <DataTable.Title
                                numeric
                                style={styles.mediumColumn}
                                sortDirection={
                                    sortBy === "id" ? sortDirection : undefined
                                }
                                onPress={() => onSortChange("id")}
                            >
                                {t("backoffice.saft.id", "ID")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.veryLargeColumn}
                                sortDirection={
                                    sortBy === "shiftId"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("shiftId")}
                            >
                                {t("backoffice.saft.shift_id", "Shift")}
                            </DataTable.Title>
                            <DataTable.Title style={styles.largeColumn}>
                                {t("backoffice.saft.merchant", "Merchant")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.smallColumn}
                                numeric
                                sortDirection={
                                    sortBy === "attempts"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("attempts")}
                            >
                                {t("backoffice.saft.attempts", "Attempts")}
                            </DataTable.Title>
                            <DataTable.Title
                                sortDirection={
                                    sortBy === "lastError"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("lastError")}
                            >
                                {t("backoffice.saft.last_error", "Last error")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.largeColumn}
                                numeric
                                sortDirection={
                                    sortBy === "runAt"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("runAt")}
                            >
                                {t("backoffice.saft.run_at", "Run at")}
                            </DataTable.Title>
                            <DataTable.Title
                                style={styles.largeColumn}
                                numeric
                                sortDirection={
                                    sortBy === "createdAt"
                                        ? sortDirection
                                        : undefined
                                }
                                onPress={() => onSortChange("createdAt")}
                            >
                                {t("common.created_at", "Created at")}
                            </DataTable.Title>
                        </DataTable.Header>
                        {loading ? (
                            <Loading />
                        ) : (
                            data?.saftJobs.data.map(renderJobRow)
                        )}
                        <DataTable.Pagination
                            onPageChange={onPageChange}
                            pageSize={pageSize}
                            onSizeChange={onPageSizeChange}
                            page={page}
                            numberOfPages={data?.saftJobs.pagination.pages}
                            itemCount={data?.saftJobs.pagination.resultCount}
                        />
                    </DataTable>
                )}
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
    smallColumn: {
        flexBasis: 80,
        flexGrow: 0,
        flexShrink: 0,
    },

    mediumColumn: {
        flexBasis: 130,
        flexGrow: 0,
        flexShrink: 0,
    },

    largeColumn: {
        flexBasis: 230,
        flexGrow: 0,
        flexShrink: 0,
    },
    veryLargeColumn: {
        flexBasis: 330,
        flexGrow: 0,
        flexShrink: 0,
    },
    iconColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 4,
    },
    container: {
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});
